import React, { createContext, PropsWithChildren, useContext, useState } from 'react';
import { useImmer } from 'use-immer';
import {
  DistributionTypeEnum,
  FixedPriceAcceptOfferInDTO,
  LocationTimeslotQuoteDemandOutDTO,
  MakeOfferInDTO,
  MyQuoteInfoOutDTO,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { DemandDetailView } from '@app/pages/carrier/demand-detail/types.ts';
import { CreateSpotState } from '@app/pages/carrier/demand-detail/DemandDetailComponent.tsx';
import { acceptFixedPriceBody, makeAnotherQuoteBody, makeSpotOfferBody } from '@app/pages/carrier/demand-detail/carrierQuoteMapping.ts';

interface DemandDetailContextType {
  BackendValidationComponent: any;
  state: DemandDetailView;
  rejectQuote: (customerId: string, applicationId: string, quoteId: string, type: DistributionTypeEnum) => void;
  makeQuote: (customerId: string, applicationId: string, quoteId: string, data: CreateSpotState, type: DistributionTypeEnum, onFinish?: () => void) => void;
  createQuote: (customerId: string, applicationId: string, quote: MyQuoteInfoOutDTO, timeslots: LocationTimeslotQuoteDemandOutDTO[]) => void;
  reconfirm: (companyId: string, applicationId: string, onSuccess?: () => void) => void;
  loading: boolean;
}

export const DemandDetailContext = createContext<DemandDetailContextType>(undefined);

interface ComponentProps {
  initialData: DemandDetailView;
}

const DemandDataProvider: React.FC<PropsWithChildren<ComponentProps>> = ({ initialData, children }) => {
  const [state, setState] = useImmer<DemandDetailView>(initialData);
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const { caAcceptFixedPrice, caGetDemandDetail, caRejectFixedPrice, caMakeQuote, caCancelDemand, caMakeAnotherNewQuote, caReconfirmedExpedition } =
    useContext(ApiContext);

  const [loading, setLoading] = useState(false);

  const methods: DemandDetailContextType = {
    BackendValidationComponent,
    state,
    rejectQuote: (customerId: string, applicationId: string, quoteId: string, type: DistributionTypeEnum) => {
      switch (type) {
        case DistributionTypeEnum.SPOT:
          caCancelDemand(customerId, applicationId, quoteId, () => {
            caGetDemandDetail(customerId, applicationId, data => {
              setState(draft => data);
            });
          });
          break;
        case DistributionTypeEnum.FIXED_PRICE:
          caRejectFixedPrice(customerId, applicationId, quoteId, () => {
            caGetDemandDetail(customerId, applicationId, data => {
              setState(draft => data);
            });
          });
          break;
        default:
          // Do nothing
          break;
      }
    },
    makeQuote: (customerId: string, applicationId: string, quoteId: string, data: CreateSpotState, type: DistributionTypeEnum, onFinish?: () => void) => {
      switch (type) {
        case DistributionTypeEnum.SPOT: {
          const body: MakeOfferInDTO = makeSpotOfferBody(data);
          caMakeQuote(
            customerId,
            applicationId,
            quoteId,
            body,
            () => {
              caGetDemandDetail(customerId, applicationId, data => {
                setState(draft => data);
                onFinish();
              });
            },
            null,
            {
              onValidationFailed: v => {
                setBeValidationErrors(v);
                onFinish();
              },
            },
          );
          break;
        }
        case DistributionTypeEnum.FIXED_PRICE: {
          const body: FixedPriceAcceptOfferInDTO = acceptFixedPriceBody(data);
          caAcceptFixedPrice(
            customerId,
            applicationId,
            quoteId,
            body,
            () => {
              caGetDemandDetail(customerId, applicationId, data => {
                setState(draft => data);
                onFinish();
              });
            },
            null,
            {
              onValidationFailed: errors => {
                setBeValidationErrors(errors);
                onFinish();
              },
            },
          );
        }
      }

    },
    createQuote: (customerId: string, applicationId: string, quote: MyQuoteInfoOutDTO, timeslots: LocationTimeslotQuoteDemandOutDTO[]) => {
      const body: MakeOfferInDTO = makeAnotherQuoteBody(quote, timeslots);

      caMakeAnotherNewQuote(customerId, applicationId, body, () => {
        caGetDemandDetail(customerId, applicationId, data => {
          setState(draft => data);
        });
      });
    },
    reconfirm: (companyId: string, applicationId: string, onSuccess: () => void = () => {}) => {
      caReconfirmedExpedition(companyId, applicationId, () => {
        caGetDemandDetail(companyId, applicationId, data => {
          setState(draft => data);
          onSuccess();
        });
      });
    },
    loading,
  };

  return <DemandDetailContext.Provider value={methods}>{children}</DemandDetailContext.Provider>;
};

export default DemandDataProvider;
